import {
  CheckboxField,
  DateRangePickerField,
  Divider,
  Form,
  NumericFormatField,
  type OperationType,
  SelectField,
  SubmitButton,
  TextInputField,
} from '@/components';
import { useForm } from '@/hooks';
import { zodResolver } from '@hookform/resolvers/zod';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import {
  useExpenditureNatures,
  useNationalExpenditureNatures,
} from '../../hooks';
import { ExpenditureNatureFormSchema } from '../../models';
import {
  EXPENDITURE_NATURE_EXTERNAL_SYSTEM_CODE_MAX_LENGTH,
  expenditureNatureCodeLength,
} from '../../utils';
import { expenditureNatureNameWithCode } from '../../utils/';
import { getCodeMaxValue } from '../../utils/get-code-max-value';

type FormValues = z.infer<typeof ExpenditureNatureFormSchema>;

type Props = {
  type: OperationType;
  initialValues?: Partial<FormValues>;
  onSubmit: (values: FormValues) => void;
};

export const ExpenditureNatureForm: FC<Props> = ({
  type,
  initialValues,
  onSubmit,
}) => {
  const { t } = useTranslation();

  const {
    handleSubmit,
    control,
    watch,
    resetField,
    formState: { errors, isSubmitting, isDirty, isSubmitSuccessful },
  } = useForm<FormValues>({
    defaultValues: {
      aggregateByExpenditureNatureForExternalSystem: false,
      ...initialValues,
    },
    resolver: zodResolver(ExpenditureNatureFormSchema),
  });

  const selectedLevel = watch('level');
  const nationalExpenditureNatureOptions = useNationalExpenditureNatures(
    (data) =>
      data.map((d) => {
        return {
          value: d.id,
          label: expenditureNatureNameWithCode({
            code: d.code,
            name: d.name,
          }),
        };
      }),
  );
  const expenditureNatureOptions = useExpenditureNatures({
    select: (data) =>
      data.flatMap((p) =>
        selectedLevel && p.level === selectedLevel - 1
          ? {
              label: expenditureNatureNameWithCode({
                code: p.code,
                name: p.name,
                level: p.level,
              }),
              value: p.id,
            }
          : [],
      ),
  });

  const levelOptions = [
    {
      label: t('expenditure-nature-level-2'),
      value: 1,
    },
    {
      label: t('expenditure-nature-level-3'),
      value: 2,
    },
  ];

  return (
    <Form
      leavePageAlertCondition={{
        isDirty,
        isSubmitting,
        isSubmitSuccessful,
      }}
      layout="vertical"
      onFinish={handleSubmit(onSubmit)}
    >
      <SelectField
        name="level"
        label={t('level')}
        control={control}
        options={levelOptions}
        errors={errors}
        disabled={type === 'edit'}
        shouldCoexistOnChange
        onChange={() => {
          resetField('code');
        }}
        required
      />

      <SelectField
        name="nationalExpenditureNatureId"
        label={t('expenditure-nature-level-1')}
        control={control}
        options={nationalExpenditureNatureOptions.data}
        errors={errors}
        disabled={selectedLevel !== 1}
      />

      <SelectField
        name="upperId"
        label={t('upper-expenditure-nature')}
        control={control}
        options={expenditureNatureOptions.data}
        errors={errors}
        disabled={selectedLevel === 1}
      />

      <NumericFormatField
        name="code"
        label={t('code')}
        control={control}
        errors={errors}
        disableSeparator
        max={getCodeMaxValue(expenditureNatureCodeLength(selectedLevel === 2))}
        required
        disabled={!selectedLevel}
      />

      <TextInputField
        name="name"
        autocompleteKey="expenditureNature.name"
        label={t('name')}
        control={control}
        errors={errors}
        required
      />

      <DateRangePickerField
        name="period"
        label={t('start-and-end-year')}
        control={control}
        errors={errors}
        picker="year"
        allowEmpty={[true, true]}
      />

      <Divider />

      <TextInputField
        name="externalSystemCode"
        autocompleteKey="expenditureNature.externalSystemCode"
        label={t('expenditure-nature-external-system-code')}
        control={control}
        errors={errors}
        maxLength={EXPENDITURE_NATURE_EXTERNAL_SYSTEM_CODE_MAX_LENGTH}
      />

      <CheckboxField
        name="aggregateByExpenditureNatureForExternalSystem"
        text={t('aggregation-target-expenditure-nature')}
        hint={t('hint-expenditure-nature-aggregation-target')}
        control={control}
        errors={errors}
      />

      <TextInputField
        name="aggregationName"
        autocompleteKey="expenditureNature.aggregationName"
        label={t('aggregation-name')}
        control={control}
        errors={errors}
      />

      <SubmitButton loading={isSubmitting} disabled={!isDirty} />
    </Form>
  );
};
